/**
 * SheetHistoriesPage - 用紙発注履歴
 * Search and display order sheet histories by date range.
 * Uses OrderSheetHistoryList organism (grouped table: order header + details + 合計 row).
 */

import React, { useCallback, useMemo, useState } from 'react';
import { Controller, type FieldErrors } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTrimmedForm } from '@/shared/lib/useTrimmedForm';
import dayjs from 'dayjs';
import { AppForm, AppButton, AppFormLabel, AppAlert } from '@/components/atoms';
import { CollapsibleCard } from '@/components/molecules';
import { RA2NDatePicker } from '@/components/organisms';
import { OrderSheetHistoryList } from '@/components/organisms/OrderSheetHistoryList';
import { useOrderSheetHistoriesQuery } from '@/hooks/main/useOrderSheetHistories';
import useAppToast from '@/hooks/useAppToast';
import {
  orderSheetHistorySearchSchema,
  type OrderSheetHistorySearchFormData,
} from '@/schemas/orderSheetHistorySchema';
import { convGtJDate, dateValueToIso } from '@/utils/dateConverter';
import type { OrderSheetHistorySearchParams } from './types';

const DEFAULT_FROM = convGtJDate(dayjs().subtract(1, 'month'));
const DEFAULT_TO = convGtJDate(dayjs());
const ERROR_MESSAGE = 'データの取得に失敗しました。';

const SheetHistoriesPage: React.FC = () => {
  const [searchParams, setSearchParams] = useState<OrderSheetHistorySearchParams | null>(null);
  const toast = useAppToast();

  const form = useTrimmedForm<OrderSheetHistorySearchFormData>({
    resolver: zodResolver(orderSheetHistorySearchSchema),
    defaultValues: {
      search_order_date_from: DEFAULT_FROM,
      search_order_date_to: DEFAULT_TO,
    },
    mode: 'onBlur',
  });

  const { data, isLoading, isError } = useOrderSheetHistoriesQuery(searchParams);

  const orders = useMemo(() => {
    const apiOrders = data?.success && data?.data?.data ? data.data.data : [];
    if (apiOrders.length > 0) return apiOrders;
    return [];
  }, [data]);

  const onSearch = useCallback(() => {
    form.handleSubmit(
      values => {
        const from = dateValueToIso(values.search_order_date_from);
        const to = dateValueToIso(values.search_order_date_to);
        if (from && to) {
          setSearchParams({ search_order_date_from: from, search_order_date_to: to });
        }
      },
      (errors: FieldErrors<OrderSheetHistorySearchFormData>) => {
        const firstError =
          errors.search_order_date_from?.message ?? errors.search_order_date_to?.message;
        if (firstError) toast.error(String(firstError));
      }
    )();
  }, [form, toast]);

  return (
    <div className="space-y-4">
      <CollapsibleCard title="用紙発注履歴">
        <AppForm layout="vertical" className="w-full">
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)_auto] lg:items-start">
            <div className="space-y-1">
              <AppFormLabel label="発注日開始日" />
              <Controller
                name="search_order_date_from"
                control={form.control}
                render={({ field, fieldState }) => (
                  <AppForm.Item validateStatus={fieldState.invalid ? 'error' : ''} className="mb-0">
                    <RA2NDatePicker
                      className="w-full"
                      value={field.value}
                      onChange={field.onChange}
                      error={fieldState.invalid}
                    />
                  </AppForm.Item>
                )}
              />
            </div>

            <div className="hidden pt-8 lg:flex lg:items-center lg:justify-center">
              <span className="text-sm text-gray-600">～</span>
            </div>

            <div className="space-y-1">
              <AppFormLabel label="発注日終了日" />
              <Controller
                name="search_order_date_to"
                control={form.control}
                render={({ field, fieldState }) => (
                  <AppForm.Item validateStatus={fieldState.invalid ? 'error' : ''} className="mb-0">
                    <RA2NDatePicker
                      className="w-full"
                      value={field.value}
                      onChange={field.onChange}
                      error={fieldState.invalid}
                    />
                  </AppForm.Item>
                )}
              />
            </div>

            <div className="flex lg:pt-9">
              <AppButton
                type="primary"
                htmlType="button"
                onClick={onSearch}
                icon={<i className="fa fa-search" />}
                className="w-full sm:w-auto"
              >
                検索
              </AppButton>
            </div>
          </div>
        </AppForm>
      </CollapsibleCard>

      {isError && <AppAlert title="エラー" description={ERROR_MESSAGE} type="error" showIcon />}

      <OrderSheetHistoryList
        orders={orders}
        loading={isLoading}
        hasSearched={searchParams !== null}
      />
    </div>
  );
};

export default SheetHistoriesPage;
